'use client';

import { useState, useEffect, useCallback } from 'react';
import { soma } from '@/lib/api';
import { BookOpen, X, Plus, Trash2 } from 'lucide-react';

export function ScratchpadPanel() {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [quickNote, setQuickNote] = useState('');
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    soma.scratchRead()
      .then((data: any) => { setContent(data.content || ''); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (open && !loaded) load();
  }, [open, loaded, load]);

  const addNote = async () => {
    if (!quickNote.trim()) return;
    await soma.scratchAppend(quickNote.trim());
    setQuickNote('');
    load();
  };

  const save = async () => {
    await soma.scratchWrite(content);
  };

  const clear = async () => {
    await soma.scratchWrite('');
    setContent('');
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-colors"
        title="Scratchpad"
      >
        <BookOpen className="w-3 h-3" />
        <span>Notes</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-gray-900 border border-[rgb(var(--border))]/60 rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[rgb(var(--border))]/40">
            <span className="text-[10px] text-gray-500 uppercase tracking-wider">Scratchpad</span>
            <div className="flex items-center gap-1">
              <button onClick={clear} className="p-1 text-gray-600 hover:text-red-400 transition-colors" title="Clear">
                <Trash2 className="w-3 h-3" />
              </button>
              <button onClick={() => setOpen(false)} className="p-1 text-gray-600 hover:text-gray-300 transition-colors">
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Quick add */}
          <form onSubmit={e => { e.preventDefault(); addNote(); }} className="flex gap-1 px-3 py-2 border-b border-[rgb(var(--border))]/20">
            <input
              value={quickNote}
              onChange={e => setQuickNote(e.target.value)}
              placeholder="Quick note..."
              className="flex-1 bg-transparent text-xs text-gray-300 placeholder:text-gray-600 focus:outline-none"
              autoFocus
            />
            <button type="submit" className="p-1 text-gray-600 hover:text-cyan-400 transition-colors">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </form>

          {/* Content */}
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            onBlur={save}
            className="w-full h-48 px-3 py-2 bg-transparent text-xs text-gray-300 font-mono resize-none focus:outline-none"
            placeholder="Your notes here..."
          />
        </div>
      )}
    </>
  );
}
